// --- shared color palette ---
// Free workspaces get a limited preset palette. Pro/Business get the full
// preset palette plus custom hex colors.

export const FREE_COLORS = [
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#84cc16", // lime
  "#10b981", // emerald
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#8b5cf6", // violet
] as const

export const PRO_COLORS = [
  ...FREE_COLORS,
  "#f43f5e", // rose
  "#d946ef", // fuchsia
  "#a855f7", // purple
  "#6366f1", // indigo
  "#0ea5e9", // sky
  "#14b8a6", // teal
  "#22c55e", // green
  "#eab308", // yellow
  "#71717a", // zinc
] as const

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/

export function isValidColor(color: string | null | undefined): color is string {
  return !!color && HEX_REGEX.test(color)
}

export function hexToRgb(hex: string) {
  const n = hex.replace("#", "")
  return {
    r: parseInt(n.slice(0, 2), 16) / 255,
    g: parseInt(n.slice(2, 4), 16) / 255,
    b: parseInt(n.slice(4, 6), 16) / 255,
  }
}

export function rgbToHex(r: number, g: number, b: number) {
  const toHex = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255))).toString(16).padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toLowerCase()
}

export function mixColor(color: string, base: string, ratio: number) {
  const c = hexToRgb(color)
  const b = hexToRgb(base)
  return rgbToHex(c.r * ratio + b.r * (1 - ratio), c.g * ratio + b.g * (1 - ratio), c.b * ratio + b.b * (1 - ratio))
}

export function getBadgeColors(color: string, theme: "light" | "dark") {
  const cardBg = theme === "dark" ? "#1e293b" : "#ffffff"
  const bg = mixColor(color, cardBg, 0.18)
  const text = getContrastColor(bg)
  return { bg, text }
}

export const GRADIENTS = [
  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "linear-gradient(135deg, #ff9a9e 0%, #fecfef 99%, #fecfef 100%)",
  "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
  "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
  "linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)",
  "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
  "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
  "linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)",
]

export function isGradient(value: string) {
  return /^linear-gradient\(|^radial-gradient\(/.test(value)
}

export function parseLinearGradient(value: string): { angle: number; from: string; to: string } | null {
  const match = value.match(/linear-gradient\((\d+)deg,\s*(#[0-9a-fA-F]{6})\s*0%,\s*(#[0-9a-fA-F]{6})\s*100%\)/)
  if (!match) return null
  return { angle: Number(match[1]), from: match[2].toLowerCase(), to: match[3].toLowerCase() }
}

export function buildLinearGradient(angle: number, from: string, to: string) {
  return `linear-gradient(${angle}deg, ${from} 0%, ${to} 100%)`
}

export function isAllowedColor(plan: "free" | "pro" | "business", color: string): boolean {
  if (isValidColor(color)) {
    if (FREE_COLORS.includes(color as any)) return true
    if (plan === "free") return false
    if (PRO_COLORS.includes(color as any)) return true
    return true // pro/business can use any custom hex
  }
  if (isGradient(color)) return plan !== "free"
  return false
}

export function getPaletteForPlan(plan: "free" | "pro" | "business"): readonly string[] {
  return plan === "free" ? FREE_COLORS : PRO_COLORS
}

export function canUseCustomColors(plan: "free" | "pro" | "business"): boolean {
  return plan !== "free"
}

// Default label color cycles through the free palette so free users always get
// a visible color without needing to choose.
export function getDefaultColor(index: number): string {
  return FREE_COLORS[index % FREE_COLORS.length]
}

// Deterministic avatar color from a user ID or email.
// Same input always yields the same color, so a user's avatar is consistent
// across sessions and devices.
export function getAvatarColor(id: string): string {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0
  }
  return FREE_COLORS[Math.abs(hash) % FREE_COLORS.length]
}

// Simple contrast helper: returns white or black foreground for a solid badge.
export function getContrastColor(bg: string): "#ffffff" | "#000000" {
  const hex = bg.replace("#", "")
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.55 ? "#000000" : "#ffffff"
}
